import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, FileText, LogOut, RefreshCw } from 'lucide-react';
import { PortalLoginForm } from '@/components/portal/PortalLoginForm';
import { MembershipOverviewCard } from '@/components/portal/MembershipOverviewCard';
import { PaymentHistoryTable } from '@/components/portal/PaymentHistoryTable';
import { VisitsTimelineSection } from '@/components/portal/VisitsTimelineSection';
import { BookVisitModal } from '@/components/portal/BookVisitModal';
import { UpdatePaymentModal } from '@/components/portal/UpdatePaymentModal';
import { UpdateProfileModal } from '@/components/portal/UpdateProfileModal';
import { CancelMembershipModal } from '@/components/portal/CancelMembershipModal';
import TermsModal from '@/components/TermsModal';
import { detectBrandFromHost, getBrandInfo, type BrandKey } from '@/lib/brand-utils';
import {
  Membership,
  Transaction,
  Visit,
  getTransactions,
  getVisits,
  getMembership,
} from '@/lib/portal-api-client';

export function MembershipPortalPage() {
  const brandKey: BrandKey = detectBrandFromHost();
  const brandInfo = useMemo(() => getBrandInfo(brandKey), [brandKey]);

  useEffect(() => {
    document.title = `${brandInfo.name} Member Portal`;
  }, [brandInfo.name]);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [membershipId, setMembershipId] = useState('');
  const [membership, setMembership] = useState<Membership | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [upcomingVisits, setUpcomingVisits] = useState<Visit[]>([]);
  const [pastVisits, setPastVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [quoteRefreshNonce, setQuoteRefreshNonce] = useState(0);

  const [showTerms, setShowTerms] = useState(false);

  // Modal states
  const [showBookVisit, setShowBookVisit] = useState(false);
  const [showUpdatePayment, setShowUpdatePayment] = useState(false);
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);
  const [showCancelMembership, setShowCancelMembership] = useState(false);

  async function handleLoginSuccess(id: string, membershipData: Membership) {
    setMembershipId(id);
    setMembership(membershipData);
    setIsLoggedIn(true);
    await loadDashboardData(id);
  }

  async function loadDashboardData(id: string) {
    setLoading(true);
    setDashboardError(null);
    try {
      const [txns, visits] = await Promise.all([
        getTransactions(id),
        getVisits(id),
      ]);
      setTransactions(txns.transactions);
      setUpcomingVisits(visits.upcoming);
      setPastVisits(visits.past);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      const message = error instanceof Error
        ? error.message
        : 'Failed to load your membership dashboard. Please try again.';
      setDashboardError(message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    if (membershipId) {
      setDashboardError(null);
      try {
        const updated = await getMembership(membershipId);
        setMembership(updated.membership);
        await loadDashboardData(membershipId);
        setQuoteRefreshNonce((n) => n + 1);
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : 'Failed to refresh your portal data. Please try again.';
        setDashboardError(message);
      }
    }
  }

  function handleLogout() {
    if (window.confirm('Are you sure you want to log out?')) {
      setIsLoggedIn(false);
      setMembershipId('');
      setMembership(null);
      setTransactions([]);
      setUpcomingVisits([]);
      setPastVisits([]);
    }
  }

  // Show login form if not logged in
  if (!isLoggedIn) {
    return <PortalLoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  // Show dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Membership</h1>
              <p className="text-gray-600 mt-1">
                Manage your membership, payments, and service appointments
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTerms(true)}
                className="px-4 py-2 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Terms & Conditions
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:border-red-300 hover:text-red-600 transition-colors flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        {dashboardError && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">We couldn’t load your portal data</div>
              <div className="text-sm">{dashboardError}</div>
            </div>
          </div>
        )}
        {membership ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Membership Overview */}
            <div className="lg:col-span-1 space-y-6">
              <MembershipOverviewCard
                membership={membership}
                onUpdatePayment={() => setShowUpdatePayment(true)}
                onUpdateProfile={() => setShowUpdateProfile(true)}
                onRequestCancellation={() => setShowCancelMembership(true)}
              />
            </div>

            {/* Right Column - Visits & Payments */}
            <div className="lg:col-span-2 space-y-6">
              {/* Visits Section */}
              <VisitsTimelineSection
                upcomingVisits={upcomingVisits}
                pastVisits={pastVisits}
                onBookVisit={() => setShowBookVisit(true)}
              />

              {/* Payment History */}
              <PaymentHistoryTable transactions={transactions} />
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading your membership data...</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {membership && (
        <>
          <BookVisitModal
            isOpen={showBookVisit}
            onClose={() => setShowBookVisit(false)}
            membershipId={membershipId}
            visitsRemaining={membership.total_visits_allowed - membership.visits_used}
            onBookingComplete={handleRefresh}
          />

          <UpdatePaymentModal
            isOpen={showUpdatePayment}
            onClose={() => setShowUpdatePayment(false)}
            membershipId={membershipId}
            onUpdateComplete={handleRefresh}
          />

          <UpdateProfileModal
            isOpen={showUpdateProfile}
            onClose={() => setShowUpdateProfile(false)}
            membershipId={membershipId}
            membership={membership}
            onUpdateComplete={handleRefresh}
          />

          <CancelMembershipModal
            isOpen={showCancelMembership}
            onClose={() => setShowCancelMembership(false)}
            membershipId={membershipId}
            membership={membership}
            quoteRefreshNonce={quoteRefreshNonce}
            onComplete={handleRefresh}
          />
        </>
      )}
    </div>
  );
}

export default MembershipPortalPage;
